import React, { useCallback, useEffect, useState } from 'react';

import { GoogleMap, useLoadScript } from '@react-google-maps/api';

import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

const containerStyle = {
  width: '100%',
  height: '600px',
};

const DEFAULT_CENTER = { lat: -33.868, lng: 151.209 };
const DEFAULT_ZOOM = 4;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GeoJsonGeometry = Record<string, any>;

export interface PlaceGeometry {
  placeId: string;
  name: string;
  type: string;
  geojson: GeoJsonGeometry;
  areaKm2: number;
}

export interface MergedGeometry {
  geojson: GeoJsonGeometry;
  areaKm2: number;
}

interface Props {
  target?: PlaceGeometry | null;
  source?: PlaceGeometry | null;
  merged?: MergedGeometry | null;
}

const LAYER_STYLES = {
  target: { strokeColor: '#1976d2', strokeWeight: 2, fillColor: '#1976d2', fillOpacity: 0.15 },
  source: { strokeColor: '#2e7d32', strokeWeight: 2, fillColor: '#2e7d32', fillOpacity: 0.15 },
  merged: { strokeColor: '#ed6c02', strokeWeight: 2, fillColor: '#ed6c02', fillOpacity: 0.3 },
} as const;

const HIDDEN_STYLE: google.maps.Data.StyleOptions = { visible: false };

function expandBoundsWithGeoJson(bounds: google.maps.LatLngBounds, geometry: GeoJsonGeometry) {
  if (geometry.type === 'Polygon') {
    geometry.coordinates[0].forEach(([lng, lat]) => bounds.extend({ lat, lng }));
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach((polygon) => {
      polygon[0].forEach(([lng, lat]) => bounds.extend({ lat, lng }));
    });
  } else if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates;
    bounds.extend({ lat: lat - 0.01, lng: lng - 0.01 });
    bounds.extend({ lat: lat + 0.01, lng: lng + 0.01 });
  }
}

type LayerKey = 'target' | 'source' | 'merged';

export default function PolygonMergePreviewMap({ target, source, merged }: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [visibleLayers, setVisibleLayers] = useState<Record<LayerKey, boolean>>({
    target: true,
    source: true,
    merged: true,
  });
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: window.configs.GOOGLE_PLACE_API_KEY,
  });

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const toggleLayer = useCallback((layer: LayerKey) => {
    setVisibleLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
  }, []);

  // Add/remove GeoJSON features when data changes
  useEffect(() => {
    if (!map) {
      return;
    }

    map.data.forEach((feature) => map.data.remove(feature));

    const bounds = new google.maps.LatLngBounds();
    let hasGeometry = false;

    if (target?.geojson) {
      map.data.addGeoJson({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: { label: 'target' }, geometry: target.geojson }],
      });
      expandBoundsWithGeoJson(bounds, target.geojson);
      hasGeometry = true;
    }

    if (source?.geojson) {
      map.data.addGeoJson({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: { label: 'source' }, geometry: source.geojson }],
      });
      expandBoundsWithGeoJson(bounds, source.geojson);
      hasGeometry = true;
    }

    if (merged?.geojson) {
      map.data.addGeoJson({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: { label: 'merged' }, geometry: merged.geojson }],
      });
      expandBoundsWithGeoJson(bounds, merged.geojson);
      hasGeometry = true;
    }

    if (hasGeometry && !bounds.isEmpty()) {
      map.fitBounds(bounds, 40);
    }
  }, [map, target, source, merged]);

  // Update styling when visibility toggles change (without re-adding features)
  useEffect(() => {
    if (!map) {
      return;
    }

    map.data.setStyle((feature) => {
      const label = feature.getProperty('label') as LayerKey;
      if (!visibleLayers[label]) {
        return HIDDEN_STYLE;
      }
      return LAYER_STYLES[label] ?? {};
    });
  }, [map, visibleLayers]);

  if (loadError) {
    return <Box>Error loading Google Maps</Box>;
  }

  if (!isLoaded) {
    return <Spinner />;
  }

  return (
    <Box>
      <GoogleMap
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        mapContainerStyle={containerStyle}
        onLoad={handleMapLoad}
      />

      <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
        {target && (
          <FormControlLabel
            control={
              <Checkbox
                checked={visibleLayers.target}
                onChange={() => toggleLayer('target')}
                size="small"
                sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
              />
            }
            label={<Typography variant="body2">Current boundary: {target.name}</Typography>}
          />
        )}
        {source && (
          <FormControlLabel
            control={
              <Checkbox
                checked={visibleLayers.source}
                onChange={() => toggleLayer('source')}
                size="small"
                sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }}
              />
            }
            label={<Typography variant="body2">Area to include: {source.name}</Typography>}
          />
        )}
        {merged && (
          <FormControlLabel
            control={
              <Checkbox
                checked={visibleLayers.merged}
                onChange={() => toggleLayer('merged')}
                size="small"
                sx={{ color: '#ed6c02', '&.Mui-checked': { color: '#ed6c02' } }}
              />
            }
            label={<Typography variant="body2">Expanded result</Typography>}
          />
        )}
      </Box>
    </Box>
  );
}
